import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { FileText, Download, Crown, Lock, Loader, CheckCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';



const ExportReports = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  const isPro = user?.subscription_tier === 'pro';
  
  const loadPreview = async () => {
    if (!isPro) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/api/reports/preview`);
      setPreviewData(response.data);
      setShowPreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };
  
  const downloadPDF = async () => {
    if (!isPro) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/api/reports/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mortgage_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };
  
  if (!isPro) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-muted rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <h4 className="font-semibold flex items-center gap-2">
                Export PDF Reports
                <Lock className="h-4 w-4 text-muted-foreground" />
              </h4>
              <p className="text-sm text-muted-foreground">Generate comprehensive mortgage reports</p>
            </div>
          </div>
          <a
            href="/dashboard?tab=settings"
            className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg text-sm hover:bg-purple-600 transition-colors"
          >
            <Crown className="h-4 w-4" />
            Upgrade to Pro
          </a>
        </div>
      </div>
    );
  }
  
  return (
    <div data-testid="export-reports" className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
            <FileText className="h-5 w-5 text-purple-400" />
          </div>
          <div>
            <h4 className="font-semibold flex items-center gap-2">
              Export PDF Reports
              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">Pro</span>
            </h4>
            <p className="text-sm text-muted-foreground">Download comprehensive mortgage analysis</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={loadPreview}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-colors disabled:opacity-50"
          >
            <Eye className="h-4 w-4" />
            Preview
          </button>
          <button
            onClick={downloadPDF}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg text-sm hover:bg-purple-600 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Download PDF
          </button>
        </div>
      </div>
      
      {/* Report Contents Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        {[
          { label: 'Mortgage Summary', desc: 'Balance, rate, equity' },
          { label: 'Amortization', desc: 'Payment schedule' },
          { label: 'Market Rates', desc: 'Current rates' },
          { label: 'HELOC Analysis', desc: 'Eligibility & options' },
          { label: 'Saved Scenarios', desc: 'All comparisons' },
          { label: 'Offers', desc: 'Tracked offers' },
          { label: 'Recommendations', desc: 'Personalized tips' },
          { label: 'Rate Trends', desc: 'Market analysis' }
        ].map((item, idx) => (
          <div key={idx} className="p-3 bg-muted/20 rounded-lg text-center">
            <p className="text-sm font-medium">{item.label}</p>
            <p className="text-xs text-muted-foreground">{item.desc}</p>
          </div>
        ))}
      </div>
      
      {/* Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Report Preview</h3>
              <button 
                onClick={() => setShowPreview(false)}
                className="p-1 hover:bg-muted rounded"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="p-3 bg-muted/20 rounded-lg">
                <p className="text-sm text-muted-foreground">Report for</p>
                <p className="font-medium">{previewData.user_name || previewData.user_email}</p>
              </div>
              
              <div>
                <p className="text-sm font-medium mb-2">Sections Included:</p>
                <ul className="space-y-1">
                  {previewData.sections?.filter(Boolean).map((section, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-400" />
                      {section}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-2 bg-muted/20 rounded">
                  <p className="text-muted-foreground">Scenarios</p>
                  <p className="font-bold">{previewData.scenarios_count}</p>
                </div>
                <div className="p-2 bg-muted/20 rounded">
                  <p className="text-muted-foreground">Offers</p>
                  <p className="font-bold">{previewData.offers_count}</p>
                </div>
              </div>
              
              <button
                onClick={() => { setShowPreview(false); downloadPDF(); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
              >
                <Download className="h-4 w-4" />
                Download Full Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportReports;
